/* Hom Muen Lee sale page — interactions for the hml-* sections.
   Every component binds through data-hml-* hooks and is idempotent, so the
   Shopify theme editor can re-run init() on shopify:section:load. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  function $all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  // marks an element as bound; the "Bound" suffix keeps it clear of the
  // data-hml-* hook attributes, which carry values (e.g. data-hml-buy="scroll")
  function once(el, key) {
    var k = key + 'Bound';
    if (el.dataset[k]) return false;
    el.dataset[k] = '1';
    return true;
  }

  /* ---------- toast ---------- */
  var toastEl, toastTimer;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'hml-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 3200);
  }

  /* ---------- reveal on scroll ---------- */
  var revealObserver =
    'IntersectionObserver' in window && !reduceMotion
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-in');
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
        )
      : null;

  function initReveal(root) {
    // children of a [data-reveal-group] get a staggered delay
    $all('[data-reveal-group]', root).forEach(function (group) {
      var step = parseFloat(group.dataset.revealGroup) || 0.08;
      $all('[data-reveal]', group).forEach(function (el, i) {
        el.style.setProperty('--d', (i * step).toFixed(2) + 's');
      });
    });
    $all('[data-reveal]', root).forEach(function (el) {
      if (!once(el, 'hmlReveal')) return;
      if (!revealObserver || designMode) el.classList.add('is-in');
      else revealObserver.observe(el);
    });
  }

  /* ---------- images fade in when loaded ---------- */
  function initImages(root) {
    $all('img.hml-img', root).forEach(function (img) {
      if (!once(img, 'hmlImg')) return;
      if (img.complete && img.naturalWidth) img.classList.add('is-loaded');
      else {
        img.addEventListener('load', function () { img.classList.add('is-loaded'); });
        img.addEventListener('error', function () { img.classList.add('is-loaded'); });
      }
    });
  }

  /* ---------- drag-to-scroll for mouse users; touch keeps native momentum ---------- */
  function enableDrag(viewport) {
    var startX = 0, startLeft = 0, moved = false, down = false;
    viewport.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true;
      moved = false;
      startX = e.clientX;
      startLeft = viewport.scrollLeft;
    });
    window.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 6) {
        moved = true;
        viewport.classList.add('is-dragging');
      }
      if (moved) viewport.scrollLeft = startLeft - dx;
    });
    window.addEventListener('pointerup', function () {
      if (!down) return;
      down = false;
      if (!moved) return;
      // let scroll-snap settle on the nearest card with a smooth glide
      var left = viewport.scrollLeft;
      viewport.classList.remove('is-dragging');
      viewport.scrollLeft = left;
      var card = nearestChild(viewport);
      if (card) viewport.scrollTo({ left: card.offsetLeft - viewportPad(viewport), behavior: 'smooth' });
    });
    viewport.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);
  }
  function viewportPad(viewport) {
    return parseFloat(getComputedStyle(viewport).scrollPaddingLeft) || 0;
  }
  function nearestChild(viewport) {
    var pad = viewportPad(viewport), best = null, bestDist = Infinity;
    Array.prototype.forEach.call(viewport.children, function (child) {
      var d = Math.abs(child.offsetLeft - pad - viewport.scrollLeft);
      if (d < bestDist) { bestDist = d; best = child; }
    });
    return best;
  }

  /* ---------- product gallery ---------- */
  function initGallery(root) {
    $all('[data-hml-gallery]', root).forEach(function (gallery) {
      if (!once(gallery, 'hmlGallery')) return;
      var viewport = gallery.querySelector('.hml-gallery__viewport');
      var slides = $all('.hml-gallery__slide', gallery);
      var dots = $all('.hml-gallery__dot', gallery);
      var thumbs = $all('.hml-gallery__thumb', gallery);
      if (!viewport || slides.length < 2) return;
      enableDrag(viewport);

      function go(i) {
        viewport.scrollTo({ left: slides[i].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      function mark(i) {
        dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); d.setAttribute('aria-current', k === i); });
        thumbs.forEach(function (t, k) { t.classList.toggle('is-active', k === i); });
      }
      var ticking = false;
      viewport.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          mark(Math.round(viewport.scrollLeft / viewport.clientWidth));
        });
      }, { passive: true });
      dots.forEach(function (d, i) { d.addEventListener('click', function () { go(i); }); });
      thumbs.forEach(function (t, i) { t.addEventListener('click', function () { go(i); }); });
      gallery.hmlGo = go;
      mark(0);
    });
  }

  /* ---------- carousels ---------- */
  function initCarousels(root) {
    $all('[data-hml-carousel]', root).forEach(function (carousel) {
      if (!once(carousel, 'hmlCarousel')) return;
      var viewport = carousel.querySelector('.hml-carousel__viewport');
      var prev = carousel.querySelector('[data-hml-prev]');
      var next = carousel.querySelector('[data-hml-next]');
      var progress = carousel.querySelector('.hml-carousel__progress span');
      if (!viewport) return;
      enableDrag(viewport);

      function step() {
        var card = viewport.children[0];
        var gap = parseFloat(getComputedStyle(viewport).columnGap) || 0;
        return card ? card.getBoundingClientRect().width + gap : viewport.clientWidth * 0.8;
      }
      function update() {
        var max = viewport.scrollWidth - viewport.clientWidth;
        var visible = viewport.clientWidth / viewport.scrollWidth;
        var p = max > 0 ? visible + (1 - visible) * (viewport.scrollLeft / max) : 1;
        if (progress) progress.parentNode.style.color = '';
        if (progress) progress.style.setProperty('--p', Math.min(1, p).toFixed(3));
        if (prev) prev.disabled = viewport.scrollLeft <= 2;
        if (next) next.disabled = viewport.scrollLeft >= max - 2;
      }
      if (prev) prev.addEventListener('click', function () { viewport.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { viewport.scrollBy({ left: step(), behavior: 'smooth' }); });
      var ticking = false;
      viewport.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () { ticking = false; update(); });
      }, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- accordions ---------- */
  function initAccordions(root) {
    $all('[data-hml-acc]', root).forEach(function (acc) {
      if (!once(acc, 'hmlAcc')) return;
      var q = acc.querySelector('.hml-acc__q');
      if (!q) return;
      q.addEventListener('click', function () {
        var open = !acc.classList.contains('is-open');
        var group = acc.closest('[data-hml-acc-group="single"]');
        if (open && group) {
          $all('.hml-acc.is-open', group).forEach(function (other) {
            other.classList.remove('is-open');
            other.querySelector('.hml-acc__q').setAttribute('aria-expanded', 'false');
          });
        }
        acc.classList.toggle('is-open', open);
        q.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ---------- bundle picker + buy ---------- */
  function money(cents, format) {
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    amount = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '฿{{amount}}').replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  function initProduct(root) {
    $all('[data-hml-product]', root).forEach(function (section) {
      if (!once(section, 'hmlProduct')) return;
      var form = section.querySelector('form[data-hml-buy-form]');
      if (!form) return;
      var priceEls = $all('[data-hml-price]', document);
      var compareEls = $all('[data-hml-compare]', document);
      var saveEls = $all('[data-hml-save]', document);
      var format = section.dataset.moneyFormat;

      function selected() {
        var input = form.querySelector('input[name="id"]:checked') || form.querySelector('input[name="id"]');
        return input;
      }
      function sync() {
        var input = selected();
        if (!input) return;
        var price = +input.dataset.price, compare = +input.dataset.compare || 0;
        $all('.hml-bundle', form).forEach(function (b) { b.classList.toggle('is-selected', b.contains(input)); });
        priceEls.forEach(function (el) { el.textContent = money(price, format); });
        compareEls.forEach(function (el) {
          el.textContent = compare > price ? money(compare, format) : '';
          el.hidden = !(compare > price);
        });
        saveEls.forEach(function (el) {
          var pct = compare > price ? Math.round((1 - price / compare) * 100) : 0;
          el.textContent = pct ? el.dataset.label.replace('{pct}', pct) : '';
          el.hidden = !pct;
        });
        // jump the gallery to the variant's own image when it has one
        var gallery = section.querySelector('[data-hml-gallery]');
        var idx = parseInt(input.dataset.mediaIndex, 10);
        if (gallery && gallery.hmlGo && idx >= 0 && section.dataset.ready) gallery.hmlGo(idx);
      }
      form.addEventListener('change', sync);
      sync();
      section.dataset.ready = '1';

      // every [data-hml-buy] on the page buys the variant picked here
      $all('[data-hml-buy]', document).forEach(function (btn) {
        if (!once(btn, 'hmlBuy')) return;
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          if (btn.dataset.hmlBuy === 'scroll') {
            form.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
            return;
          }
          buy(selected(), btn);
        });
      });

      // the sticky bar shows once the main buy button has scrolled out of view
      var sticky = document.querySelector('[data-hml-sticky]');
      var anchor = form.querySelector('.hml-buy');
      if (sticky && anchor && 'IntersectionObserver' in window) {
        var footer = document.querySelector('.hml-footer');
        var anchorVisible = true, footerVisible = false;
        var refresh = function () {
          var below = anchor.getBoundingClientRect().top < 0;
          sticky.classList.toggle('is-visible', !anchorVisible && below && !footerVisible);
        };
        new IntersectionObserver(function (en) { anchorVisible = en[0].isIntersecting; refresh(); }).observe(anchor);
        if (footer) new IntersectionObserver(function (en) { footerVisible = en[0].isIntersecting; refresh(); }).observe(footer);
      }
    });
  }

  function buy(input, btn) {
    if (!input) return;
    if (window.HML_PREVIEW) {
      toast('ตัวอย่าง: ปุ่มนี้จะพาไปหน้าชำระเงิน (บัตร / PromptPay / ปลายทาง)');
      return;
    }
    btn.classList.add('is-loading');
    var headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    // a sale page sells one pack per order, so start from an empty cart
    fetch('/cart/clear.js', { method: 'POST', headers: headers })
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({ items: [{ id: +input.value, quantity: 1 }] })
        });
      })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'add to cart failed');
        });
      })
      .then(function () { window.location.href = '/checkout'; })
      .catch(function (err) {
        btn.classList.remove('is-loading');
        toast(err.message || 'เกิดข้อผิดพลาด ลองใหม่อีกครั้ง');
      });
  }

  /* ---------- header hide on scroll down / show on scroll up ---------- */
  function initHeader() {
    var header = document.querySelector('.hml-header-section');
    if (!header || !once(header, 'hmlHeader')) return;
    var lastY = window.scrollY, ticking = false;
    function update() {
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      if (y > 240 && y > lastY + 4) header.classList.add('is-hidden');
      else if (y < lastY - 4 || y < 240) header.classList.remove('is-hidden');
      lastY = y;
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* ---------- video reviews: play while on screen, tap for sound ---------- */
  function initVideos(root) {
    var vids = $all('[data-hml-vid]', root);
    if (!vids.length) return;
    var io = 'IntersectionObserver' in window
      ? new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var v = entry.target.querySelector('video');
            if (!v) return;
            if (entry.isIntersecting && !reduceMotion) {
              if (v.preload === 'none') v.preload = 'metadata';
              var p = v.play();
              if (p && p.catch) p.catch(function () {});
            } else v.pause();
          });
        }, { threshold: 0.6 })
      : null;
    vids.forEach(function (card) {
      if (!once(card, 'hmlVid')) return;
      var v = card.querySelector('video');
      if (!v) return;
      if (io) io.observe(card);
      var btn = card.querySelector('.hml-vid__sound');
      if (btn) btn.addEventListener('click', function () {
        var unmute = v.muted;
        $all('[data-hml-vid] video').forEach(function (other) {
          other.muted = true;
          other.closest('[data-hml-vid]').classList.remove('is-unmuted');
        });
        v.muted = !unmute;
        card.classList.toggle('is-unmuted', unmute);
        if (unmute) { v.currentTime = 0; v.play(); }
      });
    });
  }

  /* ---------- reviews: show more ---------- */
  function initReviews(root) {
    $all('[data-hml-reviews]', root).forEach(function (wrap) {
      if (!once(wrap, 'hmlReviews')) return;
      var btn = wrap.querySelector('[data-hml-more]');
      if (!btn) return;
      var perClick = parseInt(btn.dataset.hmlMore, 10) || 6;
      btn.addEventListener('click', function () {
        var hidden = $all('.hml-rv[hidden]', wrap).slice(0, perClick);
        hidden.forEach(function (rv, i) {
          rv.hidden = false;
          rv.style.setProperty('--d', (i * 0.06).toFixed(2) + 's');
          if (revealObserver) {
            rv.classList.remove('is-in');
            requestAnimationFrame(function () { requestAnimationFrame(function () { rv.classList.add('is-in'); }); });
          }
        });
        if (!wrap.querySelector('.hml-rv[hidden]')) btn.parentNode.remove();
      });
    });
  }

  /* ---------- count up numbers ---------- */
  function initCounters(root) {
    $all('[data-hml-count]', root).forEach(function (el) {
      if (!once(el, 'hmlCount')) return;
      var target = parseFloat(el.dataset.hmlCount);
      var decimals = (el.dataset.hmlCount.split('.')[1] || '').length;
      if (reduceMotion || !('IntersectionObserver' in window)) { el.textContent = target.toFixed(decimals); return; }
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        var start = performance.now(), dur = 1400;
        (function tick(now) {
          var t = Math.min(1, (now - start) / dur);
          var eased = 1 - Math.pow(1 - t, 4);
          el.textContent = (target * eased).toFixed(decimals);
          if (t < 1) requestAnimationFrame(tick);
        })(start);
      });
      io.observe(el);
    });
  }

  /* ---------- review screenshots: tap to enlarge ---------- */
  var lightbox;
  function closeLightbox() {
    if (!lightbox) return;
    var box = lightbox;
    lightbox = null;
    box.classList.remove('is-open');
    setTimeout(function () { box.remove(); }, 350);
    document.removeEventListener('keydown', onLightboxKey);
  }
  function onLightboxKey(e) { if (e.key === 'Escape') closeLightbox(); }
  function openLightbox(src, alt) {
    closeLightbox();
    lightbox = document.createElement('div');
    lightbox.className = 'hml-lightbox';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.innerHTML = '<button type="button" class="hml-lightbox__close" aria-label="ปิด">✕</button><img alt="">';
    lightbox.querySelector('img').src = src;
    lightbox.querySelector('img').alt = alt || '';
    lightbox.addEventListener('click', closeLightbox);
    document.body.appendChild(lightbox);
    requestAnimationFrame(function () { lightbox && lightbox.classList.add('is-open'); });
    document.addEventListener('keydown', onLightboxKey);
  }
  function initZoom(root) {
    $all('[data-hml-zoom]', root).forEach(function (btn) {
      if (!once(btn, 'hmlZoom')) return;
      btn.addEventListener('click', function () {
        var img = btn.querySelector('img');
        openLightbox(btn.dataset.full || (img && img.src), img && img.alt);
      });
    });
  }

  function init(root) {
    root = root || document;
    initReveal(root);
    initImages(root);
    initGallery(root);
    initCarousels(root);
    initAccordions(root);
    initProduct(root);
    initVideos(root);
    initReviews(root);
    initCounters(root);
    initZoom(root);
    initHeader();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  window.HML = { init: init, toast: toast };
})();
